import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { ArrowUp, ArrowDown, Trash2, Upload, EyeOff, Building2 } from "lucide-react";
import { api, fmtError } from "@/lib/api";
import { AuthImage } from "@/components/AuthImage";

const ACCEPT = "image/jpeg,image/png,image/webp,image/svg+xml,image/heic,image/heif,.heic,.heif,.svg";
const MAX_MB = 10;

const tooBig = (f) => {
  if (f.size > MAX_MB * 1024 * 1024) { toast.error(`Fichier trop volumineux (max ${MAX_MB} Mo)`); return true; }
  return false;
};

export const LogoEditor = ({ logoUrl, onChange }) => {
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);
  const upload = async (e) => {
    const f = e.target.files?.[0]; e.target.value = "";
    if (!f || tooBig(f)) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post("/prestataires/me/logo", fd);
      onChange(data.logo_url); toast.success("Logo enregistré");
    } catch (err) { toast.error(fmtError(err)); } finally { setBusy(false); }
  };
  const remove = async () => {
    try { await api.delete("/prestataires/me/logo"); onChange(null); toast.success("Logo supprimé"); }
    catch (err) { toast.error(fmtError(err)); }
  };
  return (
    <div className="pt-4 border-t border-[#E4E7EB]" data-testid="logo-editor">
      <label className="label">Logo de l'entreprise <span className="font-normal text-[#3E4C59]">— affiché sur votre fiche et dans les résultats de recherche</span></label>
      <div className="flex items-center gap-4">
        <div className="w-24 h-24 border border-[#E4E7EB] rounded-[4px] bg-white overflow-hidden flex items-center justify-center shrink-0">
          {logoUrl
            ? <AuthImage url={logoUrl} alt="Logo" className="w-full h-full object-cover" testId="logo-preview" />
            : <Building2 size={32} className="text-[#CBD5E1]" />}
        </div>
        <div className="flex flex-col gap-2">
          <input ref={inputRef} type="file" accept={ACCEPT} className="hidden" onChange={upload} data-testid="logo-file-input" />
          <button type="button" className="btn-outline" disabled={busy} onClick={() => inputRef.current?.click()} data-testid="logo-upload-btn">
            <Upload size={14} /> {busy ? "Traitement…" : (logoUrl ? "Remplacer le logo" : "Ajouter un logo")}
          </button>
          {logoUrl && <button type="button" className="btn-ghost text-xs text-[#C0392B]" onClick={remove} data-testid="logo-delete-btn"><Trash2 size={12} /> Supprimer</button>}
          <p className="text-xs text-[#3E4C59]">jpg, png, svg, heic — recadré en carré, fond blanc.</p>
        </div>
      </div>
    </div>
  );
};

const PhotoRow = ({ photo, index, total, onLegende, onFeatured, onMove, onDelete }) => {
  const [legende, setLegende] = useState(photo.legende || "");
  useEffect(() => setLegende(photo.legende || ""), [photo.legende]);
  return (
    <div className={`flex gap-3 items-start p-2 border rounded-[4px] ${photo.hidden ? "border-[#FCA5A5] bg-[#FEF2F2]" : "border-[#E4E7EB]"}`} data-testid={`photo-row-${photo.id}`}>
      <AuthImage url={photo.thumb_url} alt={photo.legende} className="w-24 h-24 object-cover rounded-[4px] shrink-0" testId={`photo-thumb-${photo.id}`} />
      <div className="flex-1 min-w-0">
        <input className="input" maxLength={120} placeholder="Légende (ex. Ravalement, Toulouse 2026)"
          value={legende} onChange={(e) => setLegende(e.target.value)}
          onBlur={() => legende !== (photo.legende || "") && onLegende(photo.id, legende)}
          data-testid={`photo-legende-${photo.id}`} />
        {!photo.hidden && (
          <label className="mt-1.5 inline-flex items-start gap-2 text-xs text-[#3E4C59] cursor-pointer">
            <input type="checkbox" className="mt-0.5" checked={!!photo.featured}
              onChange={(e) => onFeatured(photo.id, e.target.checked)} data-testid={`photo-featured-${photo.id}`} />
            <span>Mettre en avant sur la page d'accueil <span className="text-[#94A3B8]">— visible sans connexion, avec votre raison sociale</span></span>
          </label>
        )}
        {photo.hidden && (
          <p className="mt-1 text-xs text-[#B91C1C] inline-flex items-center gap-1" data-testid={`photo-hidden-motif-${photo.id}`}>
            <EyeOff size={12} /> Masquée par l'équipe VosCompagnons : {photo.hidden_motif || "sans motif"}
          </p>
        )}
      </div>
      <div className="flex flex-col gap-1 shrink-0">
        <button type="button" className="btn-ghost p-1" disabled={index === 0} onClick={() => onMove(index, -1)} aria-label="Monter" data-testid={`photo-up-${photo.id}`}><ArrowUp size={14} /></button>
        <button type="button" className="btn-ghost p-1" disabled={index === total - 1} onClick={() => onMove(index, 1)} aria-label="Descendre" data-testid={`photo-down-${photo.id}`}><ArrowDown size={14} /></button>
        <button type="button" className="btn-ghost p-1 text-[#C0392B]" onClick={() => onDelete(photo.id)} aria-label="Supprimer" data-testid={`photo-delete-${photo.id}`}><Trash2 size={14} /></button>
      </div>
    </div>
  );
};

export const PhotosEditor = () => {
  const [state, setState] = useState({ photos: [], max: 8, rgpd_notice: "" });
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/prestataires/me/photos"); setState(data); }
    catch (err) { toast.error(fmtError(err)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const upload = async (e) => {
    const f = e.target.files?.[0]; e.target.value = "";
    if (!f || tooBig(f)) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      await api.post("/prestataires/me/photos", fd);
      toast.success("Photo ajoutée"); await load();
    } catch (err) { toast.error(fmtError(err)); } finally { setBusy(false); }
  };
  const onLegende = async (id, legende) => {
    try { await api.patch(`/prestataires/me/photos/${id}`, { legende }); await load(); }
    catch (err) { toast.error(fmtError(err)); }
  };
  const onFeatured = async (id, featured) => {
    try { await api.patch(`/prestataires/me/photos/${id}`, { featured }); await load(); }
    catch (err) { toast.error(fmtError(err)); }
  };
  const onDelete = async (id) => {
    try { await api.delete(`/prestataires/me/photos/${id}`); toast.success("Photo supprimée"); await load(); }
    catch (err) { toast.error(fmtError(err)); }
  };
  const onMove = async (index, dir) => {
    const ids = state.photos.map(p => p.id);
    const j = index + dir;
    [ids[index], ids[j]] = [ids[j], ids[index]];
    try { const { data } = await api.put("/prestataires/me/photos/order", { ids }); setState(s => ({ ...s, photos: data.photos })); }
    catch (err) { toast.error(fmtError(err)); }
  };

  const full = state.photos.length >= state.max;
  return (
    <div className="pt-4 border-t border-[#E4E7EB]" data-testid="photos-editor">
      <label className="label">Photos de chantiers et d'équipe <span className="font-normal text-[#3E4C59]">— {state.photos.length}/{state.max}</span></label>
      <div className="p-2 mb-3 bg-[#F0F4F8] border-l-4 border-[#1B3A5C] text-xs text-[#1F2933] leading-relaxed" data-testid="photos-rgpd-notice">
        {state.rgpd_notice}
      </div>
      <input ref={inputRef} type="file" accept={ACCEPT} className="hidden" onChange={upload} data-testid="photo-file-input" />
      <button type="button" className="btn-outline" disabled={busy || full} onClick={() => inputRef.current?.click()} data-testid="photo-upload-btn">
        <Upload size={14} /> {busy ? "Traitement…" : full ? `Limite de ${state.max} photos atteinte` : "Ajouter une photo"}
      </button>
      <div className="mt-3 space-y-2">
        {state.photos.map((ph, i) => (
          <PhotoRow key={ph.id} photo={ph} index={i} total={state.photos.length}
            onLegende={onLegende} onFeatured={onFeatured} onMove={onMove} onDelete={onDelete} />
        ))}
      </div>
    </div>
  );
};
